import { useCallback, useEffect, useState } from 'react';
import { Brain, DollarSign, ChevronDown, Building2 } from 'lucide-react';
import { apiClient } from '../lib/api-client';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { ActiveRunsTab } from '../components/ai-workflows/ActiveRunsTab';
import { ActivityLogTab } from '../components/ai-workflows/ActivityLogTab';
import { HumanInputsTab } from '../components/ai-workflows/HumanInputsTab';
import { StageDetailDialog } from '../components/ai-workflows/StageDetailDialog';
import { RunInspectionDialog } from '../components/ai-workflows/RunInspectionDialog';
import { ResumeRunDialog } from '../components/ai-workflows/ResumeRunDialog';
import { DeleteRunDialog } from '../components/ai-workflows/DeleteRunDialog';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs, PillTabsList, PillTabsTrigger } from '../components/ui/PillTabs';
import { Skeleton } from '../components/ui/Skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useSearchParams } from 'react-router-dom';
import { setActiveAiWorkspaceId } from '../lib/api-client';

type AiWorkflowsTab = 'runs' | 'activity' | 'sessions';

interface CostSummary {
  totalTokensInput: number;
  totalTokensOutput: number;
  totalCostUsd: number;
  runCount: number;
}

interface StreamSummary {
  streamId: string;
  displayName: string;
  enabled: boolean;
}

interface WorkspaceSummary {
  id: string;
  displayName: string;
}

export function AiWorkflows() {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspace') || 'maxiron';
  const { user } = useAuth();
  const { isAllowed: canView } = usePermission('ai_workflows', 'read');
  const { isAllowed: canIntervene } = usePermission('ai_workflows', 'intervene');
  const { isAllowed: canManage } = usePermission('ai_workflows', 'manage');

  const [streams, setStreams] = useState<StreamSummary[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [streamsLoading, setStreamsLoading] = useState(true);
  const [streamsError, setStreamsError] = useState<string | null>(null);
  const [selectedStream, setSelectedStream] = useState('');
  const [activeTab, setActiveTab] = useState<AiWorkflowsTab>('runs');
  const [costs, setCosts] = useState<CostSummary | null>(null);
  const [stageDialog, setStageDialog] = useState<{ runId: string; stageKey: string } | null>(null);
  const [inspectRunId, setInspectRunId] = useState<string | null>(null);
  const [resumeRunId, setResumeRunId] = useState<string | null>(null);
  const [deleteRunId, setDeleteRunId] = useState<string | null>(null);
  const [runsRefreshKey, setRunsRefreshKey] = useState(0);

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  if (hasNoGroups) return <NoGroupsMessage />;

  const loadStreams = useCallback(async () => {
    setActiveAiWorkspaceId(workspaceId);
    setStreamsLoading(true);
    setStreamsError(null);
    try {
      const data = await apiClient.get<{ streams: StreamSummary[] }>('/api/ai-workflows/streams');
      setStreams(data.streams || []);
    } catch (e: unknown) {
      console.error(e);
      setStreamsError(e instanceof Error ? e.message : 'Failed to load streams');
      setStreams([]);
    } finally {
      setStreamsLoading(false);
    }
  }, [workspaceId]);

  const loadWorkspaces = useCallback(async () => {
    try {
      const res = await apiClient.getAiWorkspaces();
      setWorkspaces((res.workspaces || []).map((w: any) => ({ id: w.id, displayName: w.displayName || w.id })));
    } catch {
      setWorkspaces([]);
    }
  }, []);

  const loadCosts = useCallback(async () => {
    try {
      const data = await apiClient.getAiWorkflowCosts(selectedStream ? { streamId: selectedStream } : undefined);
      setCosts(data);
    } catch {
      setCosts(null);
    }
  }, [selectedStream]);

  useEffect(() => {
    if (canView) void loadStreams();
  }, [canView, loadStreams]);

  useEffect(() => {
    if (canView) void loadWorkspaces();
  }, [canView, loadWorkspaces]);

  useEffect(() => {
    if (canView) void loadCosts();
  }, [canView, loadCosts]);

  const handleStageClick = (runId: string, stage: string) => {
    setStageDialog({ runId, stageKey: stage });
  };

  const handleResumeClick = canManage
    ? async (runId: string) => setResumeRunId(runId)
    : undefined;

  const handleDeleteRun = canManage
    ? async (runId: string) => setDeleteRunId(runId)
    : undefined;

  const handleDeleteInput = canManage
    ? async (contextId: string) => {
      try {
        await apiClient.delete(`/api/ai-workflows/human-inputs/${contextId}`);
      } catch (e: any) {
        console.error('Delete input failed:', e);
      }
    }
    : undefined;

  const streamOptions = [
    { value: '', label: 'All streams' },
    ...streams.map((s) => ({
      value: s.streamId,
      label: s.enabled ? s.displayName : `${s.displayName} (paused)`,
    })),
  ];

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Workflows</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600 dark:text-gray-300">
          You don&apos;t have permission to view AI Workflows.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Brain}
        iconColor="text-violet-600 dark:text-violet-400"
        iconBgColor="bg-violet-100 dark:bg-violet-900/30"
        description={`AI Workflows (${workspaceId}) — Monitor streams, live runs, activity, and planning sessions.`}
        count={streams.length}
        countLabel="streams"
        countColor="text-violet-600 dark:text-violet-400"
        actions={
          <div className="flex flex-wrap sm:flex-nowrap items-stretch gap-2 w-full sm:w-auto h-full">
            <DropdownMenu
              align="end"
              className="h-full"
              trigger={(
                <Button variant="outline" className="h-full justify-between gap-2 min-w-[9rem] w-full">
                  <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate text-left">
                    {(workspaces.length ? workspaces : [{ id: workspaceId, displayName: workspaceId }])
                      .find((w) => w.id === workspaceId)?.displayName || workspaceId}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0" />
                </Button>
              )}
              items={(workspaces.length ? workspaces : [{ id: workspaceId, displayName: workspaceId }]).map((w) => ({
                label: w.displayName,
                onClick: () => {
                  setSearchParams((prev) => {
                    const p = new URLSearchParams(prev);
                    p.set('workspace', w.id);
                    return p;
                  }, { replace: true });
                  setActiveAiWorkspaceId(w.id);
                },
              }))}
            />
            {streamsLoading ? (
              <Skeleton className="h-full w-40 rounded-md" />
            ) : (
              <DropdownMenu
                align="end"
                className="h-full"
                trigger={(
                  <Button variant="outline" className="h-full justify-between gap-2 min-w-[9rem] w-full">
                    <span className="truncate text-left">
                      {streamOptions.find((opt) => opt.value === selectedStream)?.label || 'All streams'}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </Button>
                )}
                items={streamOptions.map((option) => ({
                  label: option.label,
                  onClick: () => setSelectedStream(option.value),
                }))}
              />
            )}
          </div>
        }
      />

      {streamsError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {streamsError}
        </div>
      )}

      {costs && costs.runCount > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-gray-200 dark:border-gray-700">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Tracked Runs</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{costs.runCount}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200 dark:border-gray-700">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Input Tokens</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{costs.totalTokensInput.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200 dark:border-gray-700">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Output Tokens</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{costs.totalTokensOutput.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 flex items-center gap-2">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Estimated Cost</p>
                <p className="text-lg font-semibold text-green-700 dark:text-green-400">${costs.totalCostUsd.toFixed(4)}</p>
              </div>
              <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400 ml-auto" />
            </CardContent>
          </Card>
        </div>
      )}

      <PillTabs value={activeTab} onValueChange={(v) => setActiveTab(v as AiWorkflowsTab)}>
        <PillTabsList>
          <PillTabsTrigger value="runs">AI Workflow Runs</PillTabsTrigger>
          <PillTabsTrigger value="activity">Activity Log</PillTabsTrigger>
          <PillTabsTrigger value="sessions">Human Inputs</PillTabsTrigger>
        </PillTabsList>
      </PillTabs>

      {activeTab === 'runs' && (
        <ActiveRunsTab
          streamId={selectedStream}
          refreshKey={runsRefreshKey}
          onStageClick={handleStageClick}
          onInspectRun={(runId) => setInspectRunId(runId)}
          onResumeClick={handleResumeClick}
          onDeleteRun={handleDeleteRun}
        />
      )}
      {activeTab === 'activity' && <ActivityLogTab streamId={selectedStream} />}
      {activeTab === 'sessions' && (
        <HumanInputsTab streamId={selectedStream} canIntervene={canIntervene} onDeleteInput={handleDeleteInput} />
      )}

      <StageDetailDialog
        open={Boolean(stageDialog)}
        onClose={() => setStageDialog(null)}
        runId={stageDialog?.runId || ''}
        stageKey={stageDialog?.stageKey || ''}
      />
      <RunInspectionDialog
        open={Boolean(inspectRunId)}
        onClose={() => setInspectRunId(null)}
        runId={inspectRunId || ''}
      />
      <ResumeRunDialog
        open={Boolean(resumeRunId)}
        runId={resumeRunId || ''}
        onClose={() => setResumeRunId(null)}
        onCompleted={() => setRunsRefreshKey((k) => k + 1)}
      />
      <DeleteRunDialog
        open={Boolean(deleteRunId)}
        runId={deleteRunId || ''}
        onClose={() => setDeleteRunId(null)}
        onCompleted={() => setRunsRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
